import { create } from 'zustand'

type MeterType = 'water' | 'electricity'

interface ReadingsState {
  // keyed by propertyId
  readings: Record<string, Record<MeterType, Record<number, string>>>
  previousReadings: Record<string, Record<MeterType, Record<number, string>>>

  setReading: (propertyId: string, meterType: MeterType, meterId: number, value: string) => void
  setPreviousReading: (propertyId: string, meterType: MeterType, meterId: number, value: string) => void
  getReadings: (propertyId: string, meterType: MeterType) => Record<number, string>
  getPreviousReadings: (propertyId: string, meterType: MeterType) => Record<number, string>
  resetProperty: (propertyId: string) => void
  resetMeterType: (propertyId: string, meterType: MeterType) => void
  resetMeterIds: (propertyId: string, meterType: MeterType, meterIds: number[]) => void
}

export const useBulkReadingsStore = create<ReadingsState>()((set, get) => ({
  readings: {},
  previousReadings: {},

  setReading: (propertyId, meterType, meterId, value) => {
    set((state) => ({
      readings: {
        ...state.readings,
        [propertyId]: {
          ...state.readings[propertyId],
          [meterType]: {
            ...state.readings[propertyId]?.[meterType],
            [meterId]: value,
          },
        },
      },
    }))
  },

  setPreviousReading: (propertyId, meterType, meterId, value) => {
    set((state) => ({
      previousReadings: {
        ...state.previousReadings,
        [propertyId]: {
          ...state.previousReadings[propertyId],
          [meterType]: {
            ...state.previousReadings[propertyId]?.[meterType],
            [meterId]: value,
          },
        },
      },
    }))
  },

  getReadings: (propertyId, meterType) => {
    return get().readings[propertyId]?.[meterType] ?? {}
  },

  getPreviousReadings: (propertyId, meterType) => {
    return get().previousReadings[propertyId]?.[meterType] ?? {}
  },

  resetProperty: (propertyId) => {
    set((state) => {
      const readings = { ...state.readings }
      const previousReadings = { ...state.previousReadings }
      delete readings[propertyId]
      delete previousReadings[propertyId]
      return { readings, previousReadings }
    })
  },

  // Clear only one meter type for a property — used after partial-tab save so
  // the other tab's typed-but-unsaved values survive (B-06: switching n้ำ →
  // ไฟฟ้า → save was wiping the water tab's in-progress input).
  resetMeterType: (propertyId, meterType) => {
    set((state) => {
      const propReadings = state.readings[propertyId]
      const propPreviousReadings = state.previousReadings[propertyId]
      if (!propReadings && !propPreviousReadings) return state
      const nextReadings = { ...state.readings }
      const nextPreviousReadings = { ...state.previousReadings }
      if (propReadings) {
        const { [meterType]: _r, ...rest } = propReadings
        void _r
        nextReadings[propertyId] = rest as typeof propReadings
      }
      if (propPreviousReadings) {
        const { [meterType]: _p, ...rest } = propPreviousReadings
        void _p
        nextPreviousReadings[propertyId] = rest as typeof propPreviousReadings
      }
      return { readings: nextReadings, previousReadings: nextPreviousReadings }
    })
  },

  // Selectively clear typed values for ONLY the named meter IDs — used after a
  // partial-success save so locked / no_op / cascade_blocked rows retain the
  // user's input (they still need to see what they typed). Only `saved` IDs
  // should be passed in.
  resetMeterIds: (propertyId, meterType, meterIds) => {
    if (meterIds.length === 0) return
    set((state) => {
      const idSet = new Set(meterIds)
      const cloneAndStrip = (
        bucket: Record<string, Record<MeterType, Record<number, string>>>
      ) => {
        const propBucket = bucket[propertyId]
        if (!propBucket) return bucket
        const typeBucket = propBucket[meterType]
        if (!typeBucket) return bucket
        const nextTypeBucket: Record<number, string> = {}
        for (const [k, v] of Object.entries(typeBucket)) {
          if (!idSet.has(Number(k))) nextTypeBucket[Number(k)] = v
        }
        return {
          ...bucket,
          [propertyId]: { ...propBucket, [meterType]: nextTypeBucket },
        }
      }
      return {
        readings: cloneAndStrip(state.readings),
        previousReadings: cloneAndStrip(state.previousReadings),
      }
    })
  },
}))